import React, { useState } from 'react';
import axiosInstance from '../../../services/api';
import { Spinner, useToast } from '@chakra-ui/react';
import PrimaryButton from '../../../components/primaryButton/primarybutton';
import classes from './createResponse.module.css';
import states from '../../../utils/states';

const CreateResponse = ({ questions: questionsData, setTriggerClose, sid }) => {
  const [selectedCategories, setSelectedCategories] = useState({});
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const toast = useToast();

  const handleSelection = (questionId, categoryId) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [questionId]: categoryId,
    }));
  };

  const handleChange = (e, fieldName) => {
    const { type, value, checked, files } = e.target;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: type === "checkbox" ? checked : files ? files[0] : value,
    }));

    // Clear validation error when user starts typing/selecting
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const renderImageUploader = (fieldName, isRequired) => {
    const selectedImage = formData[fieldName] ? URL.createObjectURL(formData[fieldName]) : null;
    const hasError = validationErrors[fieldName];

    return (
      <div className={classes.imageUploadContainer}>
        <label style={{ display: "block", cursor: "pointer" }}>
          <img
            src={selectedImage || "https://furntech.org.za/wp-content/uploads/2017/05/placeholder-image.png"}
            alt="Upload Preview"
            className={`${classes.image} ${hasError ? classes.errorBorder : ''}`}
          />
          <input
            type="file"
            accept="image/*"
            required={isRequired}
            onChange={(e) => handleChange(e, fieldName)}
            style={{ display: "none" }}
          />
        </label>
        {!selectedImage && (
          <button 
            className={classes.uploadButton}
            onClick={() => document.querySelector(`input[name="${fieldName}"]`).click()}
          >
            Choose Image
          </button>
        )}
        {selectedImage && (
          <button 
            className={classes.uploadButton}
            onClick={() => {
              setFormData(prev => ({ ...prev, [fieldName]: undefined }));
              document.querySelector(`input[name="${fieldName}"]`).value = '';
            }}
          >
            Remove Image
          </button>
        )}
        {hasError && <p className={classes.errorText}>This field is required</p>}
      </div>
    );
  };

  const uploadImageToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'jmrkiyu0');

    try {
      const response = await fetch('https://api.cloudinary.com/v1_1/declutters/image/upload', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    questionsData.forEach((question, parentIndex) => {
      if (question.fieldType === "children") {
        if (question.isRequired && !selectedCategories[question._id]) {
          errors[question._id] = "Please select an option";
          isValid = false;
        }

        const selectedCategoryId = selectedCategories[question._id];
        if (selectedCategoryId) {
          const selectedCategory = question.childrenCategories.find(
            cat => cat._id === selectedCategoryId
          );
          
          selectedCategory.questions.forEach(childQuestion => {
            if (childQuestion.isRequired && formData[childQuestion._id] === undefined) {
              errors[childQuestion._id] = "This field is required";
              isValid = false;
            }
          });
        }
      } else {
        if (question.isRequired && formData[question.fieldName] === undefined) {
          errors[question.fieldName] = "This field is required";
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const getCoordinates = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (error) => {
            reject(error);
          }
        );
      } else {
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };

  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Please fill all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const coordinates = await getCoordinates();
      const entries = [];

      questionsData.forEach((question, parentIndex) => {
        if (question.fieldType === "children") {
          const selectedCategoryId = selectedCategories[question._id];
          if (selectedCategoryId) {
            const selectedCategory = question.childrenCategories.find(
              cat => cat._id === selectedCategoryId
            );

            entries.push({
              questionIndex: `${parentIndex}`,
              answer: selectedCategory.title,
            });

            selectedCategory.questions.forEach((childQuestion, childIndex) => {
              const answer = formData[childQuestion._id];
              if (answer !== undefined) {
                entries.push({
                  questionIndex: `${parentIndex}.${childIndex}`,
                  answer: answer,
                });
              }
            });
          }
        } else {
          const answer = formData[question.fieldName];
          if (answer !== undefined) {
            entries.push({
              questionIndex: `${parentIndex}`,
              answer: answer,
            });
          }
        }
      });

      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const questionIndexParts = entry.questionIndex.split('.');
        const parentIndex = parseInt(questionIndexParts[0], 10);
        const question = questionsData[parentIndex];

        if (question.fieldType === "children" && questionIndexParts.length > 1) {
          const childIndex = parseInt(questionIndexParts[1], 10);
          const selectedCategoryId = selectedCategories[question._id];
          const selectedCategory = question.childrenCategories.find(
            cat => cat._id === selectedCategoryId
          );
          const childQuestion = selectedCategory.questions[childIndex];

          if (childQuestion.fieldType === "image" && typeof entry.answer === "object") {
            const imageUrl = await uploadImageToCloudinary(entry.answer);
            if (imageUrl) {
              entries[i].answer = imageUrl;
            }
          }
        } else if (question.fieldType === "image" && typeof entry.answer === "object") {
          const imageUrl = await uploadImageToCloudinary(entry.answer);
          if (imageUrl) {
            entries[i].answer = imageUrl;
          }
        }
      }

      const payload = {
        surveyId: sid,
        location: coordinates,
        entries: entries,
      };

      const { data } = await axiosInstance.post('/survey-responses', payload);
      toast({
        description: data?.message,
        status: "success",
        position: "top"
      });
      setTriggerClose('close' + (Math.random()));
    } catch (error) {
      console.error('Error submitting response:', error);
      toast({
        title: 'Error submitting response',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.container}>
      {questionsData.map((question, index) => (
        <div key={question._id} className={classes.subContainer}>
          <h3 className={classes.questionTitle}>
            {question.title}
            {question.isRequired && <sup>*</sup>}
          </h3>

          {question.fieldType !== "children" && (
            <>
              {question.fieldType === "text" ||
              question.fieldType === "email" ||
              question.fieldType === "tel" ||
              question.fieldType === "date" ? (
                <>
                  <input
                    type={question.fieldType}
                    name={question.fieldName}
                    required={question.isRequired}
                    className={`${classes.textInput} ${
                      validationErrors[question.fieldName] ? classes.errorBorder : ''
                    }`}
                    onChange={(e) => handleChange(e, question.fieldName)}
                  />
                  {validationErrors[question.fieldName] && (
                    <p className={classes.errorText}>{validationErrors[question.fieldName]}</p>
                  )}
                </>
              ) : question.fieldType === "number" ? (
                <>
                  <input
                    type="number"
                    name={question.fieldName}
                    required={question.isRequired}
                    className={`${classes.textInput} ${
                      validationErrors[question.fieldName] ? classes.errorBorder : ''
                    }`}
                    onChange={(e) => handleChange(e, question.fieldName)}
                  />
                  {validationErrors[question.fieldName] && (
                    <p className={classes.errorText}>{validationErrors[question.fieldName]}</p>
                  )}
                </>
              ) : question.fieldType === "states" ? (
                <>
                  <select
                    name={question.fieldName}
                    required={question.isRequired}
                    className={`${classes.textInput} ${
                      validationErrors[question.fieldName] ? classes.errorBorder : ''
                    }`}
                    onChange={(e) => handleChange(e, question.fieldName)}
                  >
                    <option value="">Select a state</option>
                    {states.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {validationErrors[question.fieldName] && (
                    <p className={classes.errorText}>{validationErrors[question.fieldName]}</p>
                  )}
                </>
              ) : question.fieldType === "select" ? (
                <>
                  <select
                    name={question.fieldName}
                    required={question.isRequired}
                    className={`${classes.textInput} ${
                      validationErrors[question.fieldName] ? classes.errorBorder : ''
                    }`}
                    onChange={(e) => handleChange(e, question.fieldName)}
                  >
                    <option value="">Select an option</option>
                    {question.fieldOptions?.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {validationErrors[question.fieldName] && (
                    <p className={classes.errorText}>{validationErrors[question.fieldName]}</p>
                  )}
                </>
              ) : question.fieldType === "textarea" ? (
                <>
                  <textarea
                    name={question.fieldName}
                    required={question.isRequired}
                    className={`${classes.textArea} ${
                      validationErrors[question.fieldName] ? classes.errorBorder : ''
                    }`}
                    onChange={(e) => handleChange(e, question.fieldName)}
                  />
                  {validationErrors[question.fieldName] && (
                    <p className={classes.errorText}>{validationErrors[question.fieldName]}</p>
                  )}
                </>
              ) : question.fieldType === "image" ? (
                renderImageUploader(question.fieldName, question.isRequired)
              ) : question.fieldType === "checkbox" ? (
                <>
                  <input
                    type="checkbox"
                    name={question.fieldName}
                    onChange={(e) => handleChange(e, question.fieldName)}
                  />
                  {validationErrors[question.fieldName] && (
                    <p className={classes.errorText}>{validationErrors[question.fieldName]}</p>
                  )}
                </>
              ) : null}
            </>
          )}

          {question.fieldType === "children" && (
            <div>
              {validationErrors[question._id] && (
                <p className={classes.errorText}>{validationErrors[question._id]}</p>
              )}
              {question.childrenCategories.map((category) => (
                <label key={category._id} className={classes.labelRadioCon}>
                  <input
                    type="radio"
                    name={`question-${question._id}`}
                    value={category._id}
                    checked={selectedCategories[question._id] === category._id}
                    onChange={() => handleSelection(question._id, category._id)}
                  />
                  <p className={classes.labelRadioName}>{category.title}</p>
                </label>
              ))}
            </div>
          )}

          {question.fieldType === "children" &&
            selectedCategories[question._id] &&
            question.childrenCategories
              .find((cat) => cat._id === selectedCategories[question._id])
              ?.questions.map((subQ) => (
                <div key={subQ._id} style={{ paddingLeft: "20px", marginTop: "5px" }}>
                  <p className={classes.questionTitle}>
                    {subQ.question}
                    {subQ.isRequired && <sup>*</sup>}
                  </p>

                  {subQ.fieldType === "select" ? (
                    <>
                      <select
                        className={`${classes.textInput} ${
                          validationErrors[subQ._id] ? classes.errorBorder : ''
                        }`}
                        required={subQ.isRequired}
                        onChange={(e) => handleChange(e, subQ._id)}
                      >
                        <option value="">Select an option</option>
                        {subQ.fieldOptions?.map((option, index) => (
                          <option key={index} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {validationErrors[subQ._id] && (
                        <p className={classes.errorText}>{validationErrors[subQ._id]}</p>
                      )}
                    </>
                  ) : subQ.fieldType === "states" ? (
                    <>
                      <select
                        className={`${classes.textInput} ${
                          validationErrors[subQ._id] ? classes.errorBorder : ''
                        }`}
                        required={subQ.isRequired}
                        onChange={(e) => handleChange(e, subQ._id)}
                      >
                        <option value="">Select a state</option>
                        {states.map((state, index) => (
                          <option key={index} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      {validationErrors[subQ._id] && (
                        <p className={classes.errorText}>{validationErrors[subQ._id]}</p>
                      )}
                    </>
                  ) : subQ.fieldType === "number" ? (
                    <>
                      <input
                        type="number"
                        required={subQ.isRequired}
                        className={`${classes.textInput} ${
                          validationErrors[subQ._id] ? classes.errorBorder : ''
                        }`}
                        onChange={(e) => handleChange(e, subQ._id)}
                      />
                      {validationErrors[subQ._id] && (
                        <p className={classes.errorText}>{validationErrors[subQ._id]}</p>
                      )}
                    </>
                  ) : subQ.fieldType === "checkbox" ? (
                    <>
                      <input
                        type="checkbox"
                        required={subQ.isRequired}
                        onChange={(e) => handleChange(e, subQ._id)}
                      />
                      {validationErrors[subQ._id] && (
                        <p className={classes.errorText}>{validationErrors[subQ._id]}</p>
                      )}
                    </>
                  ) : subQ.fieldType === "radio" ? (
                    <>
                      {subQ.fieldOptions.map((option, index) => (
                        <label key={index} className={classes.labelRadioCon}>
                          <input
                            type="radio"
                            name={`radio-${subQ._id}`}
                            value={option}
                            required={subQ.isRequired}
                            onChange={(e) => handleChange(e, subQ._id)}
                          />
                          <p className={classes.labelRadioName}>{option}</p>
                        </label>
                      ))}
                      {validationErrors[subQ._id] && (
                        <p className={classes.errorText}>{validationErrors[subQ._id]}</p>
                      )}
                    </>
                  ) : subQ.fieldType === "textarea" ? (
                    <>
                      <textarea
                        required={subQ.isRequired}
                        className={`${classes.textArea} ${
                          validationErrors[subQ._id] ? classes.errorBorder : ''
                        }`}
                        onChange={(e) => handleChange(e, subQ._id)}
                      />
                      {validationErrors[subQ._id] && (
                        <p className={classes.errorText}>{validationErrors[subQ._id]}</p>
                      )}
                    </>
                  ) : subQ.fieldType === "image" ? (
                    renderImageUploader(subQ._id, subQ.isRequired)
                  ) : (
                    <>
                      <input
                        type={subQ.fieldType}
                        required={subQ.isRequired}
                        className={`${classes.textInput} ${
                          validationErrors[subQ._id] ? classes.errorBorder : ''
                        }`}
                        onChange={(e) => handleChange(e, subQ._id)}
                      />
                      {validationErrors[subQ._id] && (
                        <p className={classes.errorText}>{validationErrors[subQ._id]}</p>
                      )}
                    </>
                  )}
                </div>
              ))}
        </div>
      ))}
      <button className={classes.submitBtn} disabled={submitting} onClick={handleSubmit}>
        Submit {submitting && <Spinner />}
      </button>
    </div>
  );
};

export default CreateResponse;